import React from 'react'
import { Box, Typography } from '@mui/material'
import { clockIcon, pieChartIcon } from 'src/designSystem/icons'
import { isSuccessWithData, useFirebase } from 'src/firebase'
import { Program } from 'src/legacy/models/firebase'
import MeruDate from 'src/legacy/lib/MeruDate'
import { checkIfProductionEnvironment } from 'src/lib/checkIfProductionEnvironment'
import ProgramThemeEditable from './ProgramThemeEditable'
import { getThemeLabel } from './programThemeLabel'
import { useGetPatientProgressTrackerQuery } from './progressTrackerQueries.generated'

const MINUTES_PER_HOUR = 60

const formatSelfCareTime = (selfCareTimeMinutes: number): string => {
  const minutes = Math.round(selfCareTimeMinutes)
  if (minutes >= MINUTES_PER_HOUR) {
    // From an hour onwards show "8h18m"; below that just "18m".
    return `${Math.floor(minutes / MINUTES_PER_HOUR)}h${String(minutes % MINUTES_PER_HOUR).padStart(2, '0')}m`
  }
  return `${minutes}m`
}

type ProgramThemeProps = {
  lastWeekNbr: number
  weekNbr: number | null
  programThemes: Program['weeks']
}

const ProgramTheme = ({ weekNbr, lastWeekNbr, programThemes }: ProgramThemeProps) => (
  <Typography variant="h3">{getThemeLabel(weekNbr, lastWeekNbr, programThemes)}</Typography>
)

type ModuleStatsProps = {
  completionRate: number
  selfCareTimeMinutes?: number | null
}

const ProgramModuleStats = ({ completionRate, selfCareTimeMinutes }: ModuleStatsProps) => (
  <Box mt={0.5}>
    <Box display="flex" alignItems="center" gap={1}>
      <img src={pieChartIcon} alt="" width={16} height={16} />
      <Typography variant="caption">
        Completion: <strong>{Math.round(completionRate * 100)}%</strong>
      </Typography>
    </Box>
    {/* Null when usage tracking wasn't live for the whole module — hide the row
        rather than render a misleading 0m. See PX-3611. */}
    {selfCareTimeMinutes != null && (
      <Box display="flex" alignItems="center" gap={1}>
        <img src={clockIcon} alt="" width={16} height={16} />
        <Typography variant="caption">
          Self-care time: <strong>{formatSelfCareTime(selfCareTimeMinutes)}</strong>
        </Typography>
      </Box>
    )}
  </Box>
)

type Props = {
  uid: string
  programID: string
  programStartDate: string
  timezone?: string | null
}

export default function ProgramThemeInfo({ uid, programID, programStartDate, timezone }: Props) {
  const request = useFirebase<Record<string, Program>>('programs')
  const { data } = useGetPatientProgressTrackerQuery({ variables: { uid } })

  if (!isSuccessWithData(request)) return null
  const { lastWeekNbr, weeks } = request.data[programID]
  const weekNbr = MeruDate.now(timezone).formatWeekNumber(programStartDate)

  // Use isToday from server as it doesn't use "day changes at 3 AM" logic that MeruDate has
  const currentModule = data?.patient.progressTracker?.modules.find((module) => module.days.some((day) => day.isToday))

  const isProduction = checkIfProductionEnvironment()
  return (
    <Box>
      {isProduction ? (
        <ProgramTheme lastWeekNbr={lastWeekNbr} weekNbr={weekNbr} programThemes={weeks} />
      ) : (
        <ProgramThemeEditable uid={uid} lastWeekNbr={lastWeekNbr} weekNbr={weekNbr} programThemes={weeks} />
      )}
      {currentModule && (
        <ProgramModuleStats
          completionRate={currentModule.completionRate}
          selfCareTimeMinutes={currentModule.selfCareTimeMinutes}
        />
      )}
    </Box>
  )
}
